/**
 * Manipulation des jours civils, en heure de Paris.
 *
 * L'API attend `from` et `to` en `YYYY-MM-DD`, interprétés dans le fuseau
 * `Europe/Paris` — comme toutes les agrégations, et comme les crons du serveur
 * de prod. Utiliser le fuseau du navigateur produirait un décalage d'un jour
 * pour un utilisateur en déplacement, et des chiffres qui ne collent plus à
 * ceux du KPI de prod.
 */

import { ANALYTICS_TIMEZONE } from '@shared/types/stats';

// 'en-CA' produit nativement du YYYY-MM-DD — le seul format de locale qui le
// fasse, ce qui evite un assemblage manuel de formatToParts.
const parisDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ANALYTICS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Jour courant à Paris, en `YYYY-MM-DD`. */
export function todayInParis(): string {
  return parisDayFormatter.format(new Date());
}

/**
 * Portée des données d'une page : jusqu'où les chiffres existent.
 *
 * ── Deux bornes, selon la source ────────────────────────────────────────────
 * `nightly` — l'usage de l'app (section « Usage app », marqueurs). Le sync
 * tourne à 3 h du matin et ramène la journée écoulée : les données du jour en
 * cours n'existent nulle part, ni dans `user_actions`, rechargée à neuf chaque
 * nuit, ni dans `daily_snapshots`, dont la ligne du jour n'est écrite qu'au
 * sync suivant. Laisser la période courir jusqu'à aujourd'hui afficherait en
 * permanence une dernière journée à zéro — lue comme une chute d'activité, et
 * tirant vers le bas toute moyenne, verdict de marqueur compris. Borne : HIER.
 *
 * `live` — ventes, abonnements, Amazon, pub, charges, BtoB, GA4, SAV. Les
 * workers passent toutes les dix minutes à l'heure : la journée en cours est
 * en base et on veut la voir. Borne : AUJOURD'HUI (journée entamée, comparée
 * à des journées pleines — c'est voulu, et dit dans l'en-tête de page).
 *
 * La portée se déduit de l'adresse (`dataScopeForPath`) : les préréglages s'y
 * arrêtent, le sélecteur de date l'interdit au-delà, et une URL qui la dépasse
 * est ramenée dessus (cf. `usePeriodParam`). Le backend la rabat aussi, de son
 * côté — un client n'est jamais la seule garantie.
 */
export type DataScope = 'live' | 'nightly';

/** Portée des données d'une page, d'après son adresse. */
export function dataScopeForPath(pathname: string): DataScope {
  return pathname === '/usage' || pathname.startsWith('/usage/') || pathname === '/marqueurs' ? 'nightly' : 'live';
}

/** Dernier jour dont les données existent : hier (`nightly`) ou aujourd'hui (`live`). */
export function latestDataDay(scope: DataScope): string {
  const today = todayInParis();
  return scope === 'nightly' ? shiftDay(today, -1) : today;
}

/**
 * Décale un jour civil de `days` jours.
 *
 * L'arithmétique se fait en UTC sur des dates « nues » : un jour civil n'a pas
 * de fuseau, et calculer en heure locale ferait sauter ou doubler un jour lors
 * d'un changement d'heure.
 */
export function shiftDay(isoDate: string, days: number): string {
  const base = new Date(`${isoDate}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

/** Nombre de jours d'une période, bornes comprises. */
export function daysBetween(from: string, to: string): number {
  const fromMs = Date.parse(`${from}T00:00:00Z`);
  const toMs = Date.parse(`${to}T00:00:00Z`);
  return Math.round((toMs - fromMs) / 86_400_000) + 1;
}

export interface PeriodPreset {
  id: string;
  label: string;
  /** Période couverte, bornée au dernier jour de données de la portée (cf. `latestDataDay`). */
  range: (scope: DataScope) => { from: string; to: string };
  /** Portées où le préréglage a un sens ; absent : toutes. */
  scopes?: readonly DataScope[];
}

/** Les N derniers jours, dernier jour de données compris. */
export function presetRange(days: number, scope: DataScope): { from: string; to: string } {
  const to = latestDataDay(scope);
  return { from: shiftDay(to, -(days - 1)), to };
}

/** Dernier jour du mois d'une date, `YYYY-MM-DD`. */
function lastDayOfMonth(isoDate: string): string {
  const [year, month] = isoDate.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

/**
 * Borne haute d'une période SÉLECTIONNÉE : le dernier jour du mois en cours.
 *
 * ── Sélection et données sont deux choses ───────────────────────────────────
 * On peut choisir « du 1er au 30 septembre » le 25 : c'est la période qu'on
 * pilote. Les DONNÉES, elles, s'arrêtent au dernier jour de la portée
 * (`latestDataDay`) : l'API rabat la période avant de calculer, et compare
 * donc le 1er → 25 septembre au 1er → 25 août, pas à août entier. `dataPeriod`
 * donne cette période effective, et `SectionShell` affiche l'écart.
 *
 * Au-delà du mois en cours, rien : un mois futur n'aurait aucune donnée.
 */
export function maxSelectableDay(): string {
  return lastDayOfMonth(todayInParis());
}

/** Ramène un jour sous la borne de sélection (fin du mois en cours). */
export function clampToSelectable(isoDate: string): string {
  const max = maxSelectableDay();
  return isoDate > max ? max : isoDate;
}

/** Période effectivement couverte par les données : la sélection, bornée à la portée. */
export function dataPeriod(period: { from: string; to: string }, scope: DataScope): { from: string; to: string } {
  const latest = latestDataDay(scope);
  return { from: period.from > latest ? latest : period.from, to: period.to > latest ? latest : period.to };
}

/**
 * Le mois en cours, du 1er au dernier jour.
 *
 * Exception le 1er du mois, pour l'usage de l'app (sync nocturne) : le
 * nouveau mois n'a encore aucune donnée, la période est alors le mois qui
 * vient de se terminer, entier. Les données vivantes ont déjà le 1er.
 */
export function monthToDateRange(scope: DataScope): { from: string; to: string } {
  const today = todayInParis();
  const reference = today.endsWith('-01') ? latestDataDay(scope) : today;
  return { from: `${reference.slice(0, 8)}01`, to: lastDayOfMonth(reference) };
}

/** Le mois civil précédent, entier. */
export function lastMonthRange(): { from: string; to: string } {
  const firstOfThisMonth = `${todayInParis().slice(0, 8)}01`;
  const to = shiftDay(firstOfThisMonth, -1);
  return { from: `${to.slice(0, 8)}01`, to };
}

/** Aujourd'hui, seul : la journée entamée (données `live` uniquement). */
export function todayRange(): { from: string; to: string } {
  const day = todayInParis();
  return { from: day, to: day };
}

/** Hier, seul : la dernière journée pleine, quelle que soit la portée. */
export function yesterdayRange(): { from: string; to: string } {
  const day = shiftDay(todayInParis(), -1);
  return { from: day, to: day };
}

export const PERIOD_PRESETS: readonly PeriodPreset[] = [
  // Pas pour l'usage de l'app : la journée en cours n'y existe qu'au sync de la nuit.
  { id: 'today', label: "Aujourd'hui", range: () => todayRange(), scopes: ['live'] },
  { id: 'yesterday', label: 'Hier', range: () => yesterdayRange() },
  { id: '7d', label: '7 jours', range: (scope) => presetRange(7, scope) },
  { id: '30d', label: '30 jours', range: (scope) => presetRange(30, scope) },
  { id: 'mtd', label: 'Mois en cours', range: monthToDateRange },
  { id: 'lm', label: 'Mois dernier', range: () => lastMonthRange() },
  { id: '90d', label: '90 jours', range: (scope) => presetRange(90, scope) },
  { id: '12m', label: '12 mois', range: (scope) => presetRange(365, scope) },
];

/** Préréglages proposés pour une portée. */
export function presetsForScope(scope: DataScope): readonly PeriodPreset[] {
  return PERIOD_PRESETS.filter((preset) => !preset.scopes || preset.scopes.includes(scope));
}

/**
 * Ramène un jour civil sous la borne haute des données.
 *
 * Utilisé sur ce qui vient de l'extérieur — une URL partagée la semaine
 * dernière, un signet, une saisie à la main : `to=2026-09-21` ne doit pas
 * ajouter une journée vide au bout de la courbe.
 */
export function clampToLatestDay(isoDate: string, scope: DataScope): string {
  const latest = latestDataDay(scope);
  // Comparaison lexicographique : valide sur du YYYY-MM-DD zero-padde.
  return isoDate > latest ? latest : isoDate;
}
